// Which albums does an order actually have?
//
// The admin order page used to answer this from `orders.bundle`
// (Solo=1, Duo=2, Trio=3). That is wrong whenever the bundle field and the real
// album rows disagree, and they can: order #1122 was stored as Solo, priced at
// 599 (the Duo price), and had TWO albums. The page rendered one editor row, so
// album 2 had no editor, no cover picker and no way to reach it from the admin
// — invisible work that would have shipped wrong.
//
// `album_pdfs` on the order payload maps EVERY `album_sessions` row, ordered by
// album_index (worker/src/routes/orders.ts:403-409). Its indexes are therefore
// the truth about how many albums exist and what they are numbered.

export interface AlbumIndexSource {
  album_index: number;
}

/** Albums count implied by the bundle. Kept only as a fallback. */
export function bundleAlbumCount(bundle: string | null | undefined): number {
  if (bundle === 'Trio') return 3;
  if (bundle === 'Duo') return 2;
  return 1;
}

/**
 * The album numbers to render, ascending.
 *
 * Real album rows win. The bundle count is used only when there are none — which
 * happens while the order query is still loading, and for the handful of legacy
 * orders that never had album rows created. Returning the bundle's 1..N there
 * keeps those orders usable instead of showing an empty card.
 *
 * Duplicates are collapsed and non-positive/NaN indexes dropped, so a malformed
 * row cannot produce a duplicate React key or an album "0".
 */
export function resolveAlbumIndexes(
  bundle: string | null | undefined,
  albums: ReadonlyArray<AlbumIndexSource> | null | undefined,
): number[] {
  const fromRows = (albums ?? [])
    .map((a) => Number(a?.album_index))
    .filter((n) => Number.isFinite(n) && n > 0);

  if (fromRows.length > 0) {
    return Array.from(new Set(fromRows)).sort((a, b) => a - b);
  }
  return Array.from({ length: bundleAlbumCount(bundle) }, (_, i) => i + 1);
}
